/**
 * „Vorhandene manuelle Werte übergeben?“ (Board „Wirkung prüfen“): nur, wenn der Server übergebbare
 * Tage meldet – also beim Wechsel in einen Automatikmodus –, und Kästchen nur mit dem Bearbeiten-Recht.
 */
import { PRIVILEGES } from '@stayon/auth-core';
import type { PricingOwnershipAdoptionImpact, PricingOwnershipAdoptionRequest } from '@/shared/api';
import { can, useAuth } from '@/shared/auth';

interface Props {
  impact: PricingOwnershipAdoptionImpact;
  value: PricingOwnershipAdoptionRequest;
  disabled: boolean;
  onChange: (value: PricingOwnershipAdoptionRequest) => void;
}

const days = (count: number, one: string, many: string) =>
  `${count} künftige${count === 1 ? 'n' : ''} manuelle${count === 1 ? 'n' : ''} ${count === 1 ? one : many} übergeben`;

export function PricingOwnershipAdoptionControls({ impact, value, disabled, onChange }: Props) {
  // Übergeben ist dieselbe Handlung wie „an die Automatik zurückgeben“ im Kalender – der Server
  // verlangt dafür das Bearbeiten-Recht für Verfügbarkeit & Vertrieb.
  const mayAdopt = can(useAuth().user, PRIVILEGES.availabilityDistributionEdit);
  const hasRate = impact.eligibleRateDays > 0;
  const hasMinimumStay = impact.eligibleMinimumStayDays > 0;
  if (!hasRate && !hasMinimumStay) return null;
  if (!mayAdopt) return <WithoutRight impact={impact} />;
  return (
    <section className="border-hairline flex flex-col gap-2.5 rounded-lg border p-4">
      <span className="flex flex-col gap-0.5">
        <span className="text-[15px] font-bold">Vorhandene manuelle Werte übergeben?</span>
        <span className="text-ink-soft text-xs">
          Diese Tage hast du von Hand gesetzt. Ohne Haken bleiben sie, wie sie sind.
        </span>
      </span>
      {hasRate && (
        <Option
          checked={value.rate}
          disabled={disabled}
          label={days(impact.eligibleRateDays, 'Preistag', 'Preistage')}
          onChange={(checked) => onChange({ ...value, rate: checked })}
        />
      )}
      {hasMinimumStay && (
        <Option
          checked={value.minimumStay}
          disabled={disabled}
          label={days(
            impact.eligibleMinimumStayDays,
            'Mindestaufenthalt-Tag',
            'Mindestaufenthalt-Tage',
          )}
          onChange={(checked) => onChange({ ...value, minimumStay: checked })}
        />
      )}
      <span className="text-ink-soft text-xs">
        Verfügbarkeit, Buchungen, Verkaufssperren, An- und Abreisesperren und Höchstaufenthalt
        bleiben unverändert.
      </span>
    </section>
  );
}

/** Ohne das Recht fehlen die Kästchen; die Tage und der Grund bleiben sichtbar. */
function WithoutRight({ impact }: { impact: PricingOwnershipAdoptionImpact }) {
  const counts = [
    impact.eligibleRateDays > 0 && `${impact.eligibleRateDays} Preistage`,
    impact.eligibleMinimumStayDays > 0 &&
      `${impact.eligibleMinimumStayDays} Mindestaufenthalt-Tage`,
  ].filter(Boolean);
  return (
    <p className="border-hairline text-ink-soft rounded-lg border p-4 text-xs">
      Von Hand gesetzt: {counts.join(' und ')}. Sie bleiben, wie sie sind – übergeben darf nur, wer
      Verfügbarkeit &amp; Vertrieb bearbeiten darf.
    </p>
  );
}

function Option(props: {
  checked: boolean;
  disabled: boolean;
  label: string;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className="border-hairline bg-card has-[:focus-visible]:outline-forest rounded-control flex min-h-12 cursor-pointer items-center gap-3 border px-3 text-sm font-semibold has-[:focus-visible]:outline has-[:focus-visible]:outline-2">
      <input
        type="checkbox"
        className="accent-forest size-5 shrink-0"
        checked={props.checked}
        disabled={props.disabled}
        onChange={(event) => props.onChange(event.target.checked)}
      />
      {props.label}
    </label>
  );
}
